// =========================================================================
// CEMENT / MUD BALANCED PLUG SUPPORT CALCULATOR (Batch F2 — F2b)
// Four-mode support calculator for balanced plug planning. Documentation
// support only — outputs are calculation aids, not operational verdicts.
// =========================================================================
import { round, num, positive } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive };

const BBL_PER_FT3 = 1 / 5.6146;
const FT3_PER_BBL = 5.6146;
const ID2_DIVISOR = 1029.4; // bbl/ft conversion for ID² (in²)

const BASE_DISCLAIMERS = [
  "Documentation support only. Outputs are calculation aids. Review and approval by competent cementing personnel is required before any cementing operation.",
  "Hydrostatic comparison calculated from entered values. Review cementing program, displacement schedule, spacer design, and supervisor/company procedure.",
];

const ASSUMPTIONS = "Assumptions/exclusions: vertical-well; constant cross-sections; no annular friction, no compressibility, no temperature effects; spacer/contamination handling and U-tube effects per company procedure.";

function _optionalNonNegativeNumber(raw, name) {
  if (raw === "" || raw === null || raw === undefined) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) throw new Error(`Invalid number for ${name}`);
  if (n < 0) throw new Error(`${name} must be ≥ 0`);
  return n;
}

function _excessFraction(raw) {
  if (raw === "" || raw === null || raw === undefined) return 0;
  const n = Number(raw);
  if (!Number.isFinite(n)) throw new Error("Invalid number for Excess Percentage");
  if (n < 0) throw new Error("Excess Percentage must be ≥ 0");
  return n / 100;
}

function _annularCapacityBblPerFt(holeId, workstringOd) {
  // Cylinder-inside if no pipe; annulus if pipe present.
  if (workstringOd === null) return (holeId * holeId) / ID2_DIVISOR;
  if (holeId <= workstringOd) {
    throw new Error("Hole / casing ID must be greater than workstring OD for annular calculations");
  }
  return (holeId * holeId - workstringOd * workstringOd) / ID2_DIVISOR;
}

// ----- F2b: Single export with mode dispatch -----------------------------
export function balancedPlug({
  mode,
  // Mode A inputs
  holeId, workstringOd, desiredPlugLength, excessPct, slurryYieldFt3PerSk,
  // Mode B inputs
  slurryVolumeBbl,
  // Mode C inputs
  workstringId, workstringLengthFt, pumpOutputBblPerStk, pumpOutputBblPerMin,
  // Mode D inputs
  mudWeight, mudColumnLength, slurryWeight, slurryColumnLength,
  spacerWeight, spacerColumnLength, tvdReference,
}) {
  const raw = String(mode || "A").trim().toUpperCase();
  const m = raw.startsWith("MODE A") || raw === "A" ? "A"
          : raw.startsWith("MODE B") || raw === "B" ? "B"
          : raw.startsWith("MODE C") || raw === "C" ? "C"
          : raw.startsWith("MODE D") || raw === "D" ? "D"
          : "";
  if (!m) throw new Error("Mode must be 'Mode A — Plug Volume From Desired Length', 'Mode B — Plug Length From Slurry Volume', 'Mode C — Workstring Displacement Volume', or 'Mode D — Hydrostatic Balance Check'");

  // -------------------- Mode A — Plug volume from desired length -----------
  if (m === "A") {
    const ID = positive(num(holeId, "Hole / Casing Inside Diameter"), "Hole / Casing Inside Diameter");
    const OD = _optionalNonNegativeNumber(workstringOd, "Workstring Outside Diameter");
    const L = positive(num(desiredPlugLength, "Desired Plug Length"), "Desired Plug Length");
    const eFrac = _excessFraction(excessPct);
    const yield_ = _optionalNonNegativeNumber(slurryYieldFt3PerSk, "Slurry Yield");

    const cap = _annularCapacityBblPerFt(ID, OD);
    const baseVol = cap * L;
    const volExcess = baseVol * (1 + eFrac);
    const ft3 = volExcess * FT3_PER_BBL;
    const sacks = (yield_ !== null && yield_ > 0) ? ft3 / yield_ : null;
    const geometryNote = OD === null
      ? "No workstring OD entered — capacity calculated as full pipe-less inside capacity (ID² / 1029.4)."
      : "Workstring OD entered — capacity calculated as annular cross-section between hole and pipe.";

    return {
      result: round(volExcess, 2),
      unit: "bbl (Mode A — required slurry volume with excess)",
      formula: "Mode A: Capacity = (ID² − OD²) / 1029.4 ;  Volume = Capacity × Length × (1 + Excess) ;  Sacks = Volume_ft³ / Yield",
      steps: [
        "Mode A — Plug Volume From Desired Length",
        OD === null
          ? `Capacity = ${ID}² / 1029.4 = ${round(cap, 5)} bbl/ft (pipe-less, full inside)`
          : `Capacity = (${ID}² − ${OD}²) / 1029.4 = ${round(cap, 5)} bbl/ft (annulus)`,
        `Base slurry volume = ${round(cap, 5)} × ${L} = ${round(baseVol, 2)} bbl`,
        `Excess fraction = ${round(eFrac * 100, 2)} %`,
        `Slurry volume with excess = ${round(baseVol, 2)} × (1 + ${round(eFrac, 4)}) = ${round(volExcess, 2)} bbl`,
        sacks !== null
          ? `Sacks = (${round(volExcess, 2)} × 5.6146) / ${yield_} = ${round(sacks, 1)} sk`
          : "Sacks: not calculated (slurry yield not entered).",
        geometryNote,
        ...BASE_DISCLAIMERS, ASSUMPTIONS,
      ],
      outputs: {
        "Annular / inside capacity (bbl/ft)": round(cap, 5),
        "Required slurry volume — base (bbl)": round(baseVol, 2),
        "Required slurry volume — with excess (bbl)": round(volExcess, 2),
        "Estimated sacks": sacks !== null ? round(sacks, 1) : "Not calculated — slurry yield not entered",
      },
      inputs: {
        "Hole / Casing ID (in)": ID, "Workstring OD (in)": OD === null ? "Not entered" : OD,
        "Desired Plug Length (ft)": L, "Excess (%)": round(eFrac * 100, 2),
        "Slurry Yield (ft³/sk)": yield_ === null ? "Not entered" : yield_,
      },
    };
  }

  // -------------------- Mode B — Plug length from slurry volume ----------
  if (m === "B") {
    const ID = positive(num(holeId, "Hole / Casing Inside Diameter"), "Hole / Casing Inside Diameter");
    const OD = _optionalNonNegativeNumber(workstringOd, "Workstring Outside Diameter");
    const V = positive(num(slurryVolumeBbl, "Slurry Volume"), "Slurry Volume");
    const eFrac = _excessFraction(excessPct);

    const cap = _annularCapacityBblPerFt(ID, OD);
    const usableV = V / (1 + eFrac);
    const length = usableV / cap;
    const geometryNote = OD === null
      ? "No workstring OD entered — capacity calculated as full pipe-less inside capacity (ID² / 1029.4)."
      : "Workstring OD entered — capacity calculated as annular cross-section between hole and pipe.";

    return {
      result: round(length, 1),
      unit: "ft (Mode B — estimated plug length)",
      formula: "Mode B: Capacity = (ID² − OD²) / 1029.4 ;  Usable_volume = Slurry_volume / (1 + Excess) ;  Length = Usable_volume / Capacity",
      steps: [
        "Mode B — Plug Length From Slurry Volume",
        OD === null
          ? `Capacity = ${ID}² / 1029.4 = ${round(cap, 5)} bbl/ft (pipe-less, full inside)`
          : `Capacity = (${ID}² − ${OD}²) / 1029.4 = ${round(cap, 5)} bbl/ft (annulus)`,
        `Excess fraction = ${round(eFrac * 100, 2)} %`,
        `Usable slurry volume (excess removed) = ${V} / (1 + ${round(eFrac, 4)}) = ${round(usableV, 2)} bbl`,
        `Estimated plug length = ${round(usableV, 2)} / ${round(cap, 5)} = ${round(length, 1)} ft`,
        geometryNote,
        ...BASE_DISCLAIMERS, ASSUMPTIONS,
      ],
      outputs: {
        "Annular / inside capacity (bbl/ft)": round(cap, 5),
        "Usable slurry volume (excess removed) (bbl)": round(usableV, 2),
        "Estimated plug length (ft)": round(length, 1),
      },
      inputs: {
        "Hole / Casing ID (in)": ID, "Workstring OD (in)": OD === null ? "Not entered" : OD,
        "Slurry Volume (bbl)": V, "Excess (%)": round(eFrac * 100, 2),
      },
    };
  }

  // -------------------- Mode C — Workstring displacement -----------------
  if (m === "C") {
    const WID = positive(num(workstringId, "Workstring Inside Diameter"), "Workstring Inside Diameter");
    const L = positive(num(workstringLengthFt, "Workstring Length"), "Workstring Length");
    const wsCap = (WID * WID) / ID2_DIVISOR;
    const disp = wsCap * L;
    const Pstk = _optionalNonNegativeNumber(pumpOutputBblPerStk, "Pump Output (bbl/stk)");
    const Pmin = _optionalNonNegativeNumber(pumpOutputBblPerMin, "Pump Output (bbl/min)");
    if (Pstk !== null && Pstk === 0) throw new Error("Pump Output (bbl/stk) must be greater than zero");
    if (Pmin !== null && Pmin === 0) throw new Error("Pump Output (bbl/min) must be greater than zero");

    const strokes = (Pstk !== null && Pstk > 0) ? disp / Pstk : null;
    const minutes = (Pmin !== null && Pmin > 0) ? disp / Pmin : null;

    return {
      result: round(disp, 2),
      unit: "bbl (Mode C — workstring displacement volume)",
      formula: "Mode C: Capacity = ID_ws² / 1029.4 ;  Volume = Capacity × Length ;  Strokes = Volume / Pump_output_per_stk ;  Time = Volume / Pump_output_per_min",
      steps: [
        "Mode C — Workstring Displacement Volume",
        `Workstring capacity = ${WID}² / 1029.4 = ${round(wsCap, 5)} bbl/ft`,
        `Displacement volume = ${round(wsCap, 5)} × ${L} = ${round(disp, 2)} bbl (≈ ${round(disp * FT3_PER_BBL, 2)} ft³)`,
        strokes !== null
          ? `Strokes to end of workstring = ${round(disp, 2)} / ${Pstk} = ${round(strokes, 0)} stk`
          : "Strokes: not calculated (pump output per stroke not entered).",
        minutes !== null
          ? `Time to end of workstring = ${round(disp, 2)} / ${Pmin} = ${round(minutes, 1)} min`
          : "Time: not calculated (pump output per minute not entered).",
        ...BASE_DISCLAIMERS, ASSUMPTIONS,
      ],
      outputs: {
        "Workstring capacity (bbl/ft)": round(wsCap, 5),
        "Displacement volume to workstring end (bbl)": round(disp, 2),
        "Strokes to end of workstring": strokes !== null ? round(strokes, 0) : "Not calculated — pump output per stroke not entered",
        "Time to end of workstring (min)": minutes !== null ? round(minutes, 1) : "Not calculated — pump output per minute not entered",
      },
      inputs: {
        "Workstring ID (in)": WID, "Workstring Length (ft)": L,
        "Pump Output (bbl/stk)": Pstk === null ? "Not entered" : Pstk,
        "Pump Output (bbl/min)": Pmin === null ? "Not entered" : Pmin,
      },
    };
  }

  // -------------------- Mode D — Hydrostatic balance check --------------
  // Mw, Lm = mud column; Sw, Ls = slurry column; Spw, Lsp = spacer column (optional).
  const Mw = positive(num(mudWeight, "Mud Weight"), "Mud Weight");
  const Lm = positive(num(mudColumnLength, "Mud Column Length"), "Mud Column Length");
  const Sw = positive(num(slurryWeight, "Slurry Weight"), "Slurry Weight");
  const Ls = positive(num(slurryColumnLength, "Slurry Column Length"), "Slurry Column Length");
  const Spw = _optionalNonNegativeNumber(spacerWeight, "Spacer Weight");
  const Lsp = _optionalNonNegativeNumber(spacerColumnLength, "Spacer Column Length");
  const TVD = _optionalNonNegativeNumber(tvdReference, "TVD Reference");

  if ((Spw !== null) !== (Lsp !== null)) {
    throw new Error("Spacer Weight and Spacer Column Length must be entered together");
  }

  const Pmud = 0.052 * Mw * Lm;
  const Pslurry = 0.052 * Sw * Ls;
  const Pspacer = (Spw !== null && Lsp !== null) ? 0.052 * Spw * Lsp : null;
  const totalCalc = Pmud + Pslurry + (Pspacer ?? 0);
  const difference = Pslurry - Pmud;

  const steps = [
    "Mode D — Hydrostatic Balance Check",
    `P_mud = 0.052 × ${Mw} × ${Lm} = ${round(Pmud, 0)} psi`,
    `P_slurry = 0.052 × ${Sw} × ${Ls} = ${round(Pslurry, 0)} psi`,
    Pspacer !== null
      ? `P_spacer = 0.052 × ${Spw} × ${Lsp} = ${round(Pspacer, 0)} psi`
      : "P_spacer: not calculated (spacer not entered).",
    `Total of entered fluid columns = ${round(totalCalc, 0)} psi`,
    `Difference (P_slurry − P_mud) = ${round(difference, 0)} psi`,
    TVD !== null ? `TVD reference recorded: ${TVD} ft (documentation only).` : "TVD reference: not entered (documentation only).",
    "Hydrostatic comparison calculated from entered values.",
    "Review cementing program, displacement schedule, spacer design, and supervisor/company procedure.",
    ...BASE_DISCLAIMERS, ASSUMPTIONS,
  ];

  return {
    result: round(totalCalc, 0),
    unit: "psi (Mode D — total of entered fluid columns)",
    formula: "Mode D: P = 0.052 × Fluid_weight × Column_length, computed for each entered fluid column.",
    steps,
    outputs: {
      "Hydrostatic pressure — mud column (psi)": round(Pmud, 0),
      "Hydrostatic pressure — slurry column (psi)": round(Pslurry, 0),
      "Hydrostatic pressure — spacer column (psi)": Pspacer !== null ? round(Pspacer, 0) : "Not calculated — spacer not entered",
      "Total of entered fluid columns (psi)": round(totalCalc, 0),
      "Difference P_slurry − P_mud (psi)": round(difference, 0),
      "Review note": "Hydrostatic comparison calculated from entered values. Review cementing program, displacement schedule, spacer design, and supervisor/company procedure.",
    },
    inputs: {
      "Mud Weight (ppg)": Mw, "Mud Column Length (ft)": Lm,
      "Slurry Weight (ppg)": Sw, "Slurry Column Length (ft)": Ls,
      "Spacer Weight (ppg)": Spw === null ? "Not entered" : Spw,
      "Spacer Column Length (ft)": Lsp === null ? "Not entered" : Lsp,
      "TVD Reference (ft)": TVD === null ? "Not entered" : TVD,
    },
  };
}

// Suppress unused-export warning — re-exported via calculators.js barrel.
// eslint-disable-next-line no-unused-vars
const _exports_ok = { balancedPlug, BBL_PER_FT3 };
